import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { environment } from "../../environments/environment";
import { Observable, BehaviorSubject, of } from "rxjs";
import { INDRAApiService } from "./api.indra.service";
import { tap, map } from "rxjs/operators";
import {INDRATranslationToggleService} from "@ibe.components/translation-toggle/translation-toggle.service";

@Injectable({
	providedIn: "root"
})
export class INDRAIbeAdminService {
	private ibeadmin_debug: boolean;
	private translationsObj: Object;
	private translationsObjEn: Object;
	private translationsByKey: Map<string, BehaviorSubject<string>>;
	private loadedModules: Set<string>;
	private translationsLanguage: string;

	private translationsENObj: Object;
	private translationsENByKey: Map<string, BehaviorSubject<string>>;
	private loadedModulesEN: Set<string>;

	private loadingStates: { [key: string]: BehaviorSubject<boolean> } = {};

	constructor(private http: HttpClient, private api: INDRAApiService, private translationToggle: INDRATranslationToggleService) {
		this.ibeadmin_debug = false; // to show/hide ibeadmin texts with asterisc mark and help development
		this.translationsObj = {};
		this.translationsObjEn = {};
		this.translationsByKey = new Map<string, BehaviorSubject<string>>();
		this.loadedModules = new Set<string>();
		this.translationsLanguage = "";

		this.translationsENObj = {};
		this.translationsENByKey = new Map<string, BehaviorSubject<string>>();
		this.loadedModulesEN = new Set<string>();
	}

	loadTranslations(scopeId: string, moduleIds: string[], language: string) {
		if (language.toLocaleLowerCase() !== "en") {
			this.loadTranslationsEN(scopeId, moduleIds);
		}

		const modulesToLoad = new Array<string>();
		moduleIds.forEach(moduleId => {
			if (
				!this.loadedModules.has(moduleId) ||
				this.translationsLanguage != language
			)
				modulesToLoad.push(moduleId);
		});

		if (!this.translationsObjEn.hasOwnProperty("ancillaries")) {
			this.http
				.post<Object[]>(
					`${environment.api_url}ibe/rest/scopes/${scopeId}/modules/translations/en`,
					{ modules: ["ancillaries"] }
				)
				.subscribe(data => {
					data.forEach(moduleTranslations => {
						if (this.translationsObjEn)
							Object.assign(this.translationsObjEn, moduleTranslations);
						else this.translationsObjEn = moduleTranslations;
					});
				});
		}

		if (modulesToLoad.length > 0) {
			return this.http
				.post<Object[]>(
					`${environment.api_url}ibe/rest/scopes/${scopeId}/modules/translations/${language}`,
					{ modules: modulesToLoad }
				)
				.subscribe(data => {
					data.forEach(moduleTranslations => {
						if (this.translationsObj)
							Object.assign(this.translationsObj, moduleTranslations);
						else this.translationsObj = moduleTranslations;
					});

					modulesToLoad.forEach(moduleId => {
						if (!this.loadedModules.has(moduleId))
							this.loadedModules.add(moduleId);
					});

					Array.from(this.translationsByKey.keys()).forEach(key => {
						this.translationsByKey.get(key).next(this.getTranslationValue(key));
					});

					this.translationsLanguage = language;
				});
		}
	}

	reloadTranslations(scopeId: string, language: string) {
		if (language.toLocaleLowerCase() !== "en") {
			this.reloadTranslationsEN(scopeId);
		}

		const moduleIds = Array.from(this.loadedModules.values());
		this.translationsLanguage = language;

		return this.http
			.post<Object[]>(
				`${environment.api_url}ibe/rest/scopes/${scopeId}/modules/translations/${language}`,
				{ modules: moduleIds }
			)
			.subscribe(data => {
				data.forEach(moduleTranslations => {
					if (this.translationsObj)
						Object.assign(this.translationsObj, moduleTranslations);
					else this.translationsObj = moduleTranslations;
				});

				Array.from(this.translationsByKey.keys()).forEach(key =>
					this.translationsByKey.get(key).next(this.getTranslationValue(key))
				);
			});
	}

	getTranslationValue(key: string, ancillarieEnCode?: boolean): string {
		let translationsObjAux;

    if(this.translationToggle.getValue()) {
      return key;
    }
		
		if (ancillarieEnCode) translationsObjAux = this.translationsObjEn;
		else translationsObjAux = this.translationsObj;

		if (this.ibeadmin_debug)
			console.log(
				"[IBE-Admin] Getting key " +
					key +
					" with language " +
					this.translationsLanguage
			);
		if (translationsObjAux) {
			const keyValues: string[] = key.split('.');
			let result: Object = null;
			keyValues.forEach(value => {
				if (result) result = result[value];
				else result = translationsObjAux[value];
			});
			if (result) {
				if (this.ibeadmin_debug) return `*${result}*`;
				else return result.toString();
			} else {
				if (this.ibeadmin_debug)
					console.warn('[IBE-Admin] Translation key not found: "' + key + '"');
					if (this.translationsLanguage.toLocaleLowerCase() !== "en") {
					return this.getTranslationValueEN(key);
				}
				return key;
			}
		} else {
			if (this.translationsLanguage.toLocaleLowerCase() !== "en") {
				return this.getTranslationValueEN(key);
			}
			return key;
		}
	}

	getTranslationValueAsync(key: string, fromPipe: boolean = false): Observable<string> {
    if(this.translationToggle.getValue()) {
      return of(key);
    }

		if (!this.translationsByKey.has(key)) {
			const result = this.getTranslationValue(key);
			this.translationsByKey.set(key, new BehaviorSubject(result));
		}
		if (fromPipe) {
			return this.translationsByKey.get(key).asObservable();
		} else {
			return this.translationsByKey.get(key).asObservable().pipe(
				tap ((data) => {
					if (data === key) {
						this.setLoadingState(key, true)
					} else {
						this.setLoadingState(key, false)
					}
				}),
				map ((data) => {
					if (data === key) {
						return 'Loading...'
					}

					return data;
				})
			)
		}
	}

	loadTranslationsEN(scopeId: string, moduleIds: string[]) {
		const language = "en";
		const modulesToLoad = new Array<string>();
		moduleIds.forEach(moduleId => {
			if (!this.loadedModulesEN.has(moduleId)) modulesToLoad.push(moduleId);
		});

		if (modulesToLoad.length > 0) {
			return this.http
				.post<Object[]>(
					`${environment.api_url}ibe/rest/scopes/${scopeId}/modules/translations/${language}`,
					{ modules: modulesToLoad }
				)
				.subscribe(data => {
					data.forEach(moduleTranslations => {
						if (this.translationsENObj)
							Object.assign(this.translationsENObj, moduleTranslations);
						else this.translationsENObj = moduleTranslations;
					});

					modulesToLoad.forEach(moduleId => {
						if (!this.loadedModulesEN.has(moduleId))
							this.loadedModulesEN.add(moduleId);
					});

					Array.from(this.translationsENByKey.keys()).forEach(key => {
						this.translationsENByKey
							.get(key)
							.next(this.getTranslationValue(key));
					});
				});
		}
	}
	reloadTranslationsEN(scopeId: string) {
		const language = "en";
		const moduleIds = Array.from(this.loadedModulesEN.values());

		return this.http
			.post<Object[]>(
				`${environment.api_url}ibe/rest/scopes/${scopeId}/modules/translations/${language}`,
				{ modules: moduleIds }
			)
			.subscribe(data => {
				data.forEach(moduleTranslations => {
					if (this.translationsENObj)
						Object.assign(this.translationsENObj, moduleTranslations);
					else this.translationsENObj = moduleTranslations;
				});

				Array.from(this.translationsENByKey.keys()).forEach(key =>
					this.translationsENByKey.get(key).next(this.getTranslationValue(key))
				);
			});
	}
	getTranslationValueEN(key: string): string {
		if (this.ibeadmin_debug)
			console.log("[IBE-Admin] Getting key " + key + " with language en");
    if(this.translationToggle.getValue()) {
      return key;
    }
		if (this.translationsENObj) {
			const keyValues: string[] = key.split(".");
			let result: Object = null;
			keyValues.forEach(value => {
				if (result) result = result[value];
				else result = this.translationsENObj[value];
			});
			if (result) {
				if (this.ibeadmin_debug) return `*${result}*`;
				else return result.toString();
			} else {
				if (this.ibeadmin_debug)
					console.warn('[IBE-Admin] Translation key not found: "' + key + '"');
				return key;
			}
		} else return key;
	}

	getLoadingState(key: string): Observable<boolean> {
		if (!this.loadingStates[key]) {
			this.setLoadingState(key, false);
		}
		return this.loadingStates[key].asObservable();
	}

	setLoadingState(key: string, isLoading: boolean) {
		if (!this.loadingStates[key]) {
			this.loadingStates[key] = new BehaviorSubject<boolean>(isLoading);
		} else {
			this.loadingStates[key].next(isLoading);
		}
	}
}

